import { TablePagination, TableSortLabel } from '@mui/material';
import { useSearchParams } from 'react-router';
import * as React from 'react';

type ParamConfig = {
  defaults?: { [key: string]: string };
  defaultOrder?: [string, 'asc' | 'desc'];
};

export default function useModifiableParams({
  defaults = {},
  defaultOrder,
}: ParamConfig = {}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const getParams = React.useCallback((): {
    [key: string]: any;
  } => {
    const params: { [key: string]: any } = {};
    for (const [key, value] of searchParams.entries()) {
      if (value === '') continue;
      params[key] = value;

      if (key === 'page') {
        params[key] = parseInt(value, 10);
      }
    }

    if (defaultOrder) {
      if (![...searchParams.keys()].some((k) => k.startsWith('order['))) {
        params[`order[${defaultOrder[0]}]`] = defaultOrder[1];
      }
    }

    return {
      ...(defaults ?? {}),
      ...params,
    };
  }, [defaults, defaultOrder, searchParams]);

  const setModifiedParams = React.useCallback(
    (modifications: { [key: string]: string | null }) => {
      const params = new URLSearchParams(searchParams);

      if (Object.keys(modifications).some((key) => key.startsWith('order['))) {
        params.forEach((_, key) => {
          if (key.startsWith('order[')) {
            params.delete(key);
          }
        });
      }

      for (const [key, value] of Object.entries(modifications)) {
        if (value === null || value === '' || value === 'all') {
          params.delete(key);
        } else {
          params.set(key, value);
        }
      }

      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const tablePaginationProps = React.useCallback(
    ({
      totalItems,
      rowsPerPage,
    }: {
      totalItems: number | undefined;
      rowsPerPage: number;
    }): React.ComponentProps<typeof TablePagination> => {
      const params = getParams();
      return {
        count: totalItems ?? 9_999_999,
        onPageChange: (_ev, p) => {
          setModifiedParams({
            page: (p + 1).toString(),
          });
        },
        page: (params.page ?? 1) - 1,
        rowsPerPage,
        rowsPerPageOptions: [],
      };
    },
    [getParams, setModifiedParams],
  );

  const tableSortLabelProps = React.useCallback(
    (param: string): React.ComponentProps<typeof TableSortLabel> => {
      const reverseArrow = true;
      const defaultDir = 'asc';

      const fullParam = `order[${param}]`;
      const value = getParams()[fullParam];
      let arrowDir = value ?? defaultDir;
      if (reverseArrow) {
        arrowDir = arrowDir === 'asc' ? 'desc' : 'asc';
      }

      return {
        active: Boolean(value),
        direction: arrowDir,
        onClick: () => {
          setModifiedParams({
            [fullParam]: value === 'asc' ? 'desc' : 'asc',
          });
        },
      };
    },
    [getParams, setModifiedParams],
  );

  return {
    getParams,
    setModifiedParams,
    tablePaginationProps,
    tableSortLabelProps,
  };
}
